import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Mail, Menu, X } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const GetInTouch = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Store in localStorage for demo
    const messages = JSON.parse(localStorage.getItem('contact_messages') || '[]');
    messages.push({
      ...formData,
      submittedAt: new Date().toISOString()
    });
    localStorage.setItem('contact_messages', JSON.stringify(messages));

    toast.success('Thank you for reaching out! I\'ll get back to you soon.');
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-soft-light backdrop-blur-sm border-b border-taupe/20">
        <div className="container mx-auto px-4 sm:px-6 py-2 flex justify-between items-center">
          <Link to="/">
            <div className="logo-container">
              <img
                src="https://customer-assets.emergentagent.com/job_midlife-mentor/artifacts/h49dm4c7_The%20Radiant%20Phoenix%20Logo-2.png"
                alt="The Radiant Phoenix"
                className="h-20 sm:h-24 md:h-32 w-auto" />

            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/#about" className="nav-link">About</Link>
            <Link to="/work-with-me" className="nav-link">Work With Me</Link>
            <Link to="/get-in-touch" className="nav-link">Get in Touch</Link>
            <a href="https://lindairuohaofficial.substack.com/subscribe" target="_blank" rel="noopener noreferrer" className="nav-link">Subscribe</a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-900 hover:text-gold transition-colors"
            aria-label="Toggle menu">
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen &&
        <nav className="md:hidden bg-soft-light border-t border-taupe/20">
            <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
              <Link to="/" className="nav-link text-lg py-2" onClick={() => setMobileMenuOpen(false)}>
                Home
              </Link>
              <Link to="/#about" className="nav-link text-lg py-2" onClick={() => setMobileMenuOpen(false)}>
                About
              </Link>
              <Link to="/work-with-me" className="nav-link text-lg py-2" onClick={() => setMobileMenuOpen(false)}>
                Work With Me
              </Link>
              <Link to="/get-in-touch" className="nav-link text-lg py-2" onClick={() => setMobileMenuOpen(false)}>
                Get in Touch
              </Link>
              <a href="https://lindairuohaofficial.substack.com/subscribe" target="_blank" rel="noopener noreferrer" className="nav-link text-lg py-2" onClick={() => setMobileMenuOpen(false)}>
                Subscribe
              </a>
            </div>
          </nav>
        }
      </header>

      {/* Hero Section */}
      <section className="pt-28 sm:pt-36 md:pt-52 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center">
            {/* Image */}
            <div className="order-first lg:order-first">
              <img
                src="https://customer-assets.emergentagent.com/job_midlife-mentor/artifacts/231284m1_brandagency-render-38.png"
                alt="Linda Iruoha"
                className="w-full h-auto rounded-lg shadow-lg" />

            </div>

            {/* Content */}
            <div className="space-y-4 sm:space-y-6">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light font-display leading-tight text-gray-900 italic">
                Get in Touch
              </h1>
              <p className="text-base sm:text-lg md:text-xl font-body text-gray-700 leading-relaxed">
                I'd love to hear from you. Whether you have questions about The Radiant Phoenix, want to learn more about working together, or simply wish to share your journey, I'm here to listen.
              </p>
              <p className="text-base sm:text-lg md:text-xl font-body text-gray-700 leading-relaxed">
                Fill out the form below and I'll get back to you as soon as possible. Your message matters, and I look forward to connecting with you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-soft-light">
        <div className="container mx-auto max-w-2xl">
          <Card className="bg-white shadow-xl">
            <CardContent className="p-6 sm:p-8 md:p-10 lg:p-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light font-display text-gray-900 text-center mb-6 sm:mb-8 italic">
                Send Me a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div>
                  <label htmlFor="name" className="block text-base sm:text-lg font-body text-gray-700 mb-2">
                    Your Name
                  </label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full" />

                </div>

                <div>
                  <label htmlFor="email" className="block text-base sm:text-lg font-body text-gray-700 mb-2">
                    Your Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full" />

                </div>

                <div>
                  <label htmlFor="message" className="block text-base sm:text-lg font-body text-gray-700 mb-2">
                    Your Message
                  </label>
                  <Textarea
                    id="message"
                    placeholder="Share what's on your mind..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full min-h-[150px] sm:min-h-[200px]" />

                </div>

                <Button
                  type="submit"
                  className="w-full border-4 border-[#C9A84C] bg-transparent text-[#0D1B2A] hover:bg-[#C9A84C] hover:text-white py-4 sm:py-6 text-base sm:text-lg font-semibold transition-all">

                  Send Message
                </Button>
              </form>

              {/* Contact Info */}
              <div className="mt-8 sm:mt-10 md:mt-12 pt-6 sm:pt-8 border-t border-gray-200">
                <h3 className="text-lg sm:text-xl font-body font-semibold text-gray-900 mb-3 sm:mb-4 text-center">
                  Connect With Me
                </h3>
                <div className="flex justify-center gap-6 sm:gap-8 mt-4 sm:mt-6">
                  <a href="mailto:hello@theradiantphoenix.com" className="social-icon-footer hover:text-gold transition-colors">
                    <Mail size={24} className="sm:w-7 sm:h-7" />
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-10 md:py-12 px-4 sm:px-6 bg-white border-t border-taupe/20">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col items-center gap-3 sm:gap-4">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_midlife-mentor/artifacts/h49dm4c7_The%20Radiant%20Phoenix%20Logo-2.png"
                alt="The Radiant Phoenix"
                className="h-24 sm:h-32 md:h-40 w-auto" />
            </div>
            <div className="text-center">
              <p className="text-sm sm:text-base font-body text-gray-600">© 2026 The Radiant Phoenix. All rights reserved.

              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>);

};

export default GetInTouch;